import { FC, useState, useEffect } from "react";
import axios, { AxiosError, AxiosResponse } from "axios";
import {
  Grid,
  FormControl,
  TextField,
  FormControlLabel,
  Switch,
} from "@material-ui/core";

import {
  OpenEdgePaymentData,
  OpenEdgePaymentDataErrorProps,
  ErrorProps,
} from "../../../entities/PaymentOptions";
import ReactNotification, {
  SUCCESS,
  DANGER,
} from "../../../utils/ReactNotification.config";
import {
  check,
  objError,
  DETAILS_INVALID,
  STAX,
  WEB_TOKEN_ID,
  WEB_TOKEN_ID_MANDATORY,
  MERCHANT_ID,
  MERCHANT_ID_MANDATORY,
  API_KEY,
  API_KEY_MANDATORY,
  STAX_TITLE,
  STAX_ENABLED_MESSAGE,
  STAX_DISABLED_MESSAGE,
} from "./paymentMethods.const";
import { useStylesInput } from "./style";
import { api, base_url } from "../../../utils/env";
import { PaymentMode } from "../../../constants/payment.const";

interface OpenEdgeMethodsProps {
  type: string | null;
  companyID: string | null;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  data: any;
  masterID: string | null;
}

const StaxPaymentSelection: FC<OpenEdgeMethodsProps> = ({
  type,
  companyID,
  setLoading,
  data,
  masterID,
}: OpenEdgeMethodsProps) => {
  const [paymentData, setPaymentData] = useState<OpenEdgePaymentData>(check);
  const [errors, setErrors] = useState<OpenEdgePaymentDataErrorProps>(objError);
  const classesInput = useStylesInput();
  useEffect(() => {
    if (companyID && companyID !== "default") {
      setPaymentData({
        ...paymentData,
        ...check,
        ...data,
        masterId: Number(masterID),
        companyId: companyID,
        merchantId:
          data && data.paymentGateway === "Stax" && data.merchantId
            ? data.merchantId
            : "",
        webToken:
          data && data.paymentGateway === "Stax" && data.webToken
            ? data.webToken
            : "",
        apiKey:
          data && data.paymentGateway && data.paymentGateway === "Stax"
            ? data.apiKey
            : "",
      });
    } else {
      setPaymentData({
        ...paymentData,
        ...check,
        masterId: Number(masterID),
        companyId: companyID,
      });
    }
    setErrors(objError);
  }, [companyID, data, masterID]);

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    inputName: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    validation(inputName, event);
    setPaymentData({
      ...paymentData,
      ...{ [inputName]: event.currentTarget.value },
    });
  };

  const setError = (
    property: string = "",
    status: boolean = false,
    message: string = ""
  ) => {
    setErrors({
      ...errors,
      [property]: {
        status: status,
        message: message,
      },
    });
  };

  const validation = (
    property: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const value = event.currentTarget.value.trim();

    if (property === WEB_TOKEN_ID) {
      if (!value) setError(property, true, WEB_TOKEN_ID_MANDATORY);
      else setError(property);
    }

    if (property === MERCHANT_ID) {
      if (!value) setError(property, true, MERCHANT_ID_MANDATORY);
      else setError(property);
    }

    if (property === API_KEY) {
      if (!value) setError(property, true, API_KEY_MANDATORY);
      else setError(property);
    }
  };

  const onConfirmValidation = () => {
    let webTokenError: ErrorProps;
    let merchantIdError: ErrorProps;
    let apiKeyError: ErrorProps;
    let valid: boolean = false;

    if (!paymentData.webToken) {
      webTokenError = {
        status: true,
        message: WEB_TOKEN_ID_MANDATORY,
      };
      valid = true;
    } else {
      webTokenError = {
        status: false,
        message: "",
      };
    }

    if (!paymentData.merchantId) {
      merchantIdError = {
        status: true,
        message: MERCHANT_ID_MANDATORY,
      };
      valid = true;
    } else {
      merchantIdError = {
        status: false,
        message: "",
      };
    }

    if (!paymentData.apiKey) {
      apiKeyError = {
        status: true,
        message: API_KEY_MANDATORY,
      };
      valid = true;
    } else {
      apiKeyError = {
        status: false,
        message: "",
      };
    }

    setErrors({
      webToken: webTokenError,
      merchantId: merchantIdError,
      apiKey: apiKeyError,
      xWebId: webTokenError,
      terminalId: merchantIdError,
      authKey: apiKeyError,
    });
    return valid;
  };

  const updatePaymentDataStatus = (status: string) => {
    setPaymentData({
      ...paymentData,
      ...{ paymentGateway: status },
      ...{ enable: false },
    });
  };

  const updatePaymentDataStatusAndAuthKey = (
    status: string,
    newAuthKey: string
  ) => {
    setPaymentData({
      ...paymentData,
      ...{
        paymentGateway: status,
        apiKey: newAuthKey,
      },
    });
  };

  const savePaymentOptions = async (statusCode: string) => {
    setLoading(true);
    const paylod = {
      companyId: paymentData.companyId?.toString().trim(),
      masterId: masterID,
      enable: false,
      authKey: paymentData.authKey?.toString().trim(),
      terminalId: paymentData.terminalId?.toString().trim(),
      xwebId: paymentData.xWebId?.toString().trim(),
      webToken: paymentData.webToken?.toString().trim(),
      apiKey: paymentData.apiKey?.toString().trim(),
      merchantId: paymentData.merchantId?.toString().trim(),
      termsAndConditions: "",
      paymentGateway: statusCode,
    };
    const data = JSON.stringify(paylod);
    const customerServiceLinkEndUrl = `${base_url}${api}/staxpayments/create`;
    console.log(
      "Send stax payload to customerServiceLik " + customerServiceLinkEndUrl
    );
    await axios
      .post(customerServiceLinkEndUrl, data, {
        headers: {
          "content-type": "application/json",
        },
      })
      .then((response: AxiosResponse<any>) => {
        const TITLE: string = STAX_TITLE;
        let message: string;
        message = STAX_ENABLED_MESSAGE;
        if (response?.data?.paymentGateway === "Stax") {
          updatePaymentDataStatusAndAuthKey(
            PaymentMode.OPEN_EDGE,
            response.data.apiKey
          );
        } else {
          message = STAX_DISABLED_MESSAGE;
          updatePaymentDataStatusAndAuthKey(PaymentMode.OPEN_EDGE, "");
        }
        setLoading(false);
        ReactNotification.showNotification(TITLE, message, SUCCESS);
        window.location.replace(`/admin/successwarepay/`);
      })
      .catch((error: AxiosError<any>) => {
        console.error(
          "failed to send to stax endpoint" + customerServiceLinkEndUrl
        );
        console.error("error message " + error);
        setLoading(false);
        const TITLE: string = STAX;
        ReactNotification.showNotification(TITLE, DETAILS_INVALID, DANGER);

        if (statusCode === PaymentMode.STAX) {
          updatePaymentDataStatusAndAuthKey(PaymentMode.OPEN_EDGE, "");
        } else {
          updatePaymentDataStatus(PaymentMode.OPEN_EDGE);
        }
      });
  };

  const onConfirmAction = (status: string) => {
    onConfirmValidation()
      ? updatePaymentDataStatus(status)
      : savePaymentOptions(status);
  };

  const checkStaxDisable = (): boolean => {
    return !paymentData.staxEnable &&
      companyID &&
      companyID.trim() !== "" &&
      companyID !== "default"
      ? false
      : true;
  };

  return (
    <section className=" box-shadow custom-card-enable-success-pay">
      <Grid container direction="row">
        <span className="payment-options-title-label">
          {STAX_TITLE} Settings
        </span>
      </Grid>
      <div className="grid-margin">
        <Grid container direction="row" md={6} xs={12}>
          <div className="required-star">
            <FormControl
              required
              className={classesInput.formControl}
              size="small"
            >
              <TextField
                id={`x-web-${type}`}
                label="Web Token"
                name={`xWebId-${type}`}
                variant="outlined"
                size="small"
                autoComplete="off"
                inputProps={{ className: classesInput.input }}
                error={errors.webToken.status}
                value={
                  paymentData?.paymentGateway !== PaymentMode.CYBERSOURCE &&
                  paymentData.webToken
                    ? paymentData.webToken
                    : ""
                }
                helperText={errors.webToken.message}
                onChange={(
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >
                ) => onInputChange(event, WEB_TOKEN_ID)}
                onBlur={(event: any) => validation(WEB_TOKEN_ID, event)}
                disabled={checkStaxDisable()}
              />
            </FormControl>
            <div className={`required-default`}>*</div>
          </div>
        </Grid>
        <Grid container direction="row" md={6} xs={12}>
          <div className="required-star">
            <FormControl
              required
              className={classesInput.formControl}
              size="small"
            >
              <TextField
                id={`terminal-${type}`}
                label="Merchant ID"
                name={`terminalId-${type}`}
                variant="outlined"
                size="small"
                autoComplete="off"
                inputProps={{ className: classesInput.input }}
                error={errors.merchantId.status}
                value={
                  paymentData?.paymentGateway !== PaymentMode.CYBERSOURCE &&
                  paymentData.merchantId
                    ? paymentData.merchantId
                    : ""
                }
                helperText={errors.merchantId.message}
                onChange={(
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >
                ) => onInputChange(event, MERCHANT_ID)}
                onBlur={(event: any) => validation(MERCHANT_ID, event)}
                disabled={checkStaxDisable()}
              />
            </FormControl>
            <div className={`required-default`}>*</div>
          </div>
        </Grid>
        <Grid container md={12}>
          <Grid container direction="row" md={6} xs={12}>
            <div className="required-star">
              <FormControl
                required
                className={classesInput.formControl}
                size="small"
              >
                <TextField
                  id={`auth-key-${type}`}
                  label="Api Key"
                  name={`authKey-${type}`}
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  inputProps={{ className: classesInput.input }}
                  error={errors.apiKey.status}
                  value={
                    paymentData?.paymentGateway !== PaymentMode.CYBERSOURCE &&
                    paymentData.apiKey
                      ? paymentData.apiKey
                      : ""
                  }
                  helperText={errors.apiKey.message}
                  onChange={(
                    event: React.ChangeEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >
                  ) => onInputChange(event, API_KEY)}
                  onBlur={(event: any) => validation(API_KEY, event)}
                  disabled={checkStaxDisable()}
                />
              </FormControl>
              <div className={`required-default`}>*</div>
            </div>
          </Grid>
          <Grid
            container
            direction="row"
            md={6}
            xs={12}
            alignItems="center"
            justifyContent="center"
          >
            <FormControlLabel
              className={classesInput.switch}
              classes={{ label: classesInput.switchLabel }}
              control={
                <Switch
                  className="custom-switch"
                  disabled={
                    companyID &&
                    companyID.trim() !== "" &&
                    companyID !== "default"
                      ? false
                      : true
                  }
                  checked={paymentData.paymentGateway === "Stax"}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onConfirmAction(
                      paymentData?.paymentGateway === "Stax" ||
                        paymentData?.paymentGateway === "CyberSource"
                        ? PaymentMode.OPEN_EDGE
                        : PaymentMode.STAX
                    )
                  }
                  name="checkedA-switch"
                />
              }
              label={`Enable ${STAX_TITLE} Access`}
            />
          </Grid>
        </Grid>
      </div>
    </section>
  );
};

export default StaxPaymentSelection;
